'use client'

import { useEffect, useState, useRef } from 'react'
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react'

const MIN_ZOOM = 1
const MAX_ZOOM = 6
const ZOOM_STEP = 1.5

export default function ImageModal() {
  const [image, setImage] = useState<{ src: string; alt: string } | null>(null)
  const [zoom, setZoom] = useState(1)
  // Размер картинки, вписанной в экран при zoom=1
  const [fit, setFit] = useState<{ w: number; h: number } | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  const reset = () => {
    setImage(null)
    setZoom(1)
    setFit(null)
  }

  useEffect(() => {
    ;(window as any).openImageModal = (src: string, alt: string) => {
      setImage({ src, alt })
      setZoom(1)
      setFit(null)
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setImage(null)
        setZoom(1)
        setFit(null)
      }
    }
    window.addEventListener('keydown', handleEscape)

    return () => {
      delete (window as any).openImageModal
      window.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const handleLoad = () => {
    const img = imgRef.current
    if (!img) return
    const vw = window.innerWidth * 0.95
    const vh = window.innerHeight * 0.85
    const k = Math.min(vw / img.naturalWidth, vh / img.naturalHeight, 1)
    setFit({ w: Math.round(img.naturalWidth * k), h: Math.round(img.naturalHeight * k) })
  }

  // Блокируем скролл страницы, пока модалка открыта
  useEffect(() => {
    if (!image) return
    const bodyPrev = document.body.style.overflow
    const htmlPrev = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = bodyPrev
      document.documentElement.style.overflow = htmlPrev
    }
  }, [image])

  // После зума удерживаем точку просмотра в центре области прокрутки
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2
  }, [zoom, fit])

  if (!image) return null

  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, +(z * ZOOM_STEP).toFixed(2)))
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, +(z / ZOOM_STEP).toFixed(2)))

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
      onClick={reset}
    >
      {/* Controls */}
      <div
        className="absolute top-4 left-4 right-4 flex items-center justify-between z-20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors disabled:opacity-40 disabled:hover:bg-white/10"
            title="Уменьшить"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-white/70 text-sm tabular-nums min-w-[3rem] text-center select-none">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors disabled:opacity-40 disabled:hover:bg-white/10"
            title="Увеличить"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
        </div>
        <button
          onClick={reset}
          className="p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          title="Закрыть (Esc)"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Scrollable image: grid place-items-center центрирует и не съедает скролл */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        <div className="min-h-full min-w-full grid place-items-center p-6">
          <img
            ref={imgRef}
            src={image.src}
            alt={image.alt}
            onLoad={handleLoad}
            onDoubleClick={() => setZoom(z => (z > 1 ? 1 : 2))}
            style={fit ? { width: Math.round(fit.w * zoom), height: Math.round(fit.h * zoom) } : undefined}
            className="object-contain rounded-lg select-none"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      {/* Caption & Download */}
      <div
        className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-20"
        onClick={(e) => e.stopPropagation()}
      >
        {image.alt && (
          <p className="text-white/70 text-sm truncate max-w-[60%]">{image.alt}</p>
        )}
        <a
          href={image.src}
          download
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors ml-auto"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Скачать</span>
        </a>
      </div>
    </div>
  )
}
